import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { usePerspective } from '../hooks/usePerspective'
import { useAccess } from '../auth/useAccess'
import { Roles } from '../auth/rbac'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/date'
import { RequestStatusLabel } from '../lib/labels'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'
import { History, MessageSquare, Paperclip } from 'lucide-react'

export default function RequestDetailPage() {
    const { id } = useParams()
    const { user } = useAuth() || { user: null }
    const { show } = useToast()
    const { view } = usePerspective()
    const { allow } = useAccess()
    const [searchParams, setSearchParams] = useSearchParams()

    // 💡 주요 상태값
    const [detail, setDetail] = useState(null)
    const [comments, setComments] = useState([])
    const [history, setHistory] = useState([])
    const [attachments, setAttachments] = useState([])
    const [candidateManagers, setCandidateManagers] = useState([])
    const [candidateFilterAdminOrMapped, setCandidateFilterAdminOrMapped] = useState(false)
    const [targets, setTargets] = useState([])
    const [activeTargetId, setActiveTargetId] = useState(null)
    const insurerRoles = [
        Roles.INSURER_LEADER,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_MANAGER,
        Roles.INSURER_PLAN_MANAGER,
    ]
    const isInsurerUser = (user?.roles || []).some(r => insurerRoles.includes(r))
    const isInsurerAdmin = (user?.roles || []).some(r => ([
        Roles.OPERATOR,
        Roles.INSURER_LEADER,
        Roles.INSURER_BRANCH_MANAGER,
    ]).includes(r))
    const filteredTargets = React.useMemo(() => {
        if (!Array.isArray(targets)) return []
        // 원수사 사용자는 자신의 원수사 타깃만 노출
        if (isInsurerUser && user?.orgId) {
            return targets.filter(t => String(t.insurerOrgId) === String(user.orgId))
        }
        return targets
    }, [targets, isInsurerUser, user?.orgId])
    const displayTargets = React.useMemo(() => {
        // 취소된 보험사 타겟은 탭에서 가장 뒤로 보내 사용자 인지성을 높인다.
        const arr = Array.isArray(filteredTargets) ? [...filteredTargets] : []
        arr.sort((a, b) => {
            const ac = a?.status === 'CANCELED' ? 1 : 0
            const bc = b?.status === 'CANCELED' ? 1 : 0
            return ac - bc
        })
        return arr
    }, [filteredTargets])
    const currentTarget = React.useMemo(() => {
        if (!Array.isArray(filteredTargets) || filteredTargets.length === 0) return null
        if (activeTargetId == null) return filteredTargets[0]
        return filteredTargets.find(t => String(t.id) === String(activeTargetId)) || filteredTargets[0]
    }, [filteredTargets, activeTargetId])

    // 💡 UI 상태값
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [showAssignmentPanel, setShowAssignmentPanel] = useState(false)
    const [assignmentLoading, setAssignmentLoading] = useState(false)
    const [selectedManager, setSelectedManager] = useState(null)
    const [assignmentReason, setAssignmentReason] = useState('')
    const [commentText, setCommentText] = useState('')
    const [commentVisibility, setCommentVisibility] = useState('EXTERNAL')
    const [editingCommentId, setEditingCommentId] = useState(null)
    const [isQuestion, setIsQuestion] = useState(false)
    const [applyToAllTargets, setApplyToAllTargets] = useState(false)
    const [showAllComments, setShowAllComments] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [commentsScrolledOnce, setCommentsScrolledOnce] = useState(false)
    const [historyScrolledOnce, setHistoryScrolledOnce] = useState(false)
    const [autoRefreshComments, setAutoRefreshComments] = useState(false)
    const [autoScrollPending, setAutoScrollPending] = useState(false)
    const commentsListRef = useRef(null)
    const historyListRef = useRef(null)

    // 알림톡 발송 실패 상세/재발송 모달
    const [notifModalOpen, setNotifModalOpen] = useState(false)
    const [notifModalTarget, setNotifModalTarget] = useState(null) // { id, insurer... }
    const [notifDetail, setNotifDetail] = useState(null)
    const [notifLoading, setNotifLoading] = useState(false)
    const [notifError, setNotifError] = useState(null)

    const safeParseJson = (raw) => {
        if (!raw || typeof raw !== 'string') return null
        try { return JSON.parse(raw) } catch { return null }
    }

    const openNotifDetail = async (target) => {
        if (!target?.id) return
        setNotifModalTarget(target)
        setNotifModalOpen(true)
        setNotifDetail(null)
        setNotifError(null)
        setNotifLoading(true)
        try {
            const res = await api.get(`/requests/${id}/targets/${target.id}/request-assigned-notification-log`)
            const d = res.data?.data || res.data
            setNotifDetail(d)
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || null
            setNotifError(msg || '발송 이력을 불러오지 못했습니다')
        } finally {
            setNotifLoading(false)
        }
    }

    const resendNotif = async () => {
        const target = notifModalTarget
        if (!target?.id) return
        setNotifLoading(true)
        setNotifError(null)
        try {
            await api.post(`/requests/${id}/targets/${target.id}/request-assigned-notification-resend`, {})
            show('재발송을 시도했습니다', 'success')
            // 상세/타겟 상태 갱신
            const res = await api.get(`/requests/${id}`)
            const d = res.data?.data || res.data
            setDetail(d)
            const ts = Array.isArray(d?.targets) ? d.targets : []
            setTargets(ts)
            // 모달 상세도 재조회
            await openNotifDetail(target)
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || null
            show(`재발송 실패${msg ? ` (${msg})` : ''}`, 'error')
            setNotifError(msg || '재발송 실패')
        } finally {
            setNotifLoading(false)
        }
    }

    const reloadComments = useCallback(async (opts = {}) => {
        const scrollToBottom = !!opts.scrollToBottom
        try {
            const res = await api.get(`/requests/${id}/comments`)
            setComments(Array.isArray(res.data?.data) ? res.data.data : res.data?.data?.items || [])
            if (scrollToBottom) setAutoScrollPending(true)
        } catch {
            // 필요 시 토스트 추가 가능
        }
    }, [id])

    // ==================================================
    // 🔹 상세 / 코멘트 / 히스토리 / 첨부파일 / 후보자 로드
    // ==================================================
    useEffect(() => {
        let ignore = false
        async function load() {
            setLoading(true)
            setError(null)
            try {
                const res = await api.get(`/requests/${id}`)
                if (!ignore) {
                    const d = res.data?.data || res.data
                    setDetail(d)
                    const ts = Array.isArray(d?.targets) ? d.targets : []
                    setTargets(ts)
                    const fromUrl = searchParams.get('targetId')
                    setActiveTargetId(fromUrl ? Number(fromUrl) : (ts[0]?.id || null))
                }

                Promise.all([
                    api.get(`/requests/${id}/comments`).catch(() => ({ data: { data: [] } })),
                    api.get(`/requests/${id}/history`).catch(() => ({ data: { data: [] } })),
                    api.get(`/requests/${id}/attachments`).catch(() => ({ data: { data: [] } })),
                    // GA 화면에서는 candidate-managers(원수사 전용) 호출을 하지 않는다(403 노이즈 방지)
                    (isInsurerUser || isInsurerAdmin)
                        ? api.get(`/requests/${id}/candidate-managers`).catch(() => ({ data: { data: [] } }))
                        : Promise.resolve({ data: { data: [] } }),
                ]).then(([c, h, a, m]) => {
                    if (!ignore) {
                        setComments(Array.isArray(c.data?.data) ? c.data.data : c.data?.data?.items || [])
                        setHistory(Array.isArray(h.data?.data) ? h.data.data : h.data?.data?.items || [])
                        setAttachments(Array.isArray(a.data?.data) ? a.data.data : a.data?.data?.items || [])
                        setCandidateManagers(Array.isArray(m.data?.data) ? m.data.data : m.data?.data?.items || [])
                    }
                })
            } catch {
                if (!ignore) setError('상세를 불러오지 못했습니다')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => { ignore = true }
    }, [id, searchParams])

    // 코멘트 영역: 최초 진입 시 맨 아래로 스크롤
    useEffect(() => {
        if (!commentsScrolledOnce && comments.length > 0 && commentsListRef.current) {
            commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight
            setCommentsScrolledOnce(true)
        }
    }, [comments, commentsScrolledOnce])

    // 코멘트 영역: 자동 새로고침(ON)일 때는 갱신 후 항상 맨 아래(최신)로 스크롤
    useEffect(() => {
        if (autoScrollPending && commentsListRef.current) {
            commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight
            setAutoScrollPending(false)
        }
    }, [autoScrollPending, comments])

    // 코멘트 자동 새로고침 토글 (30초)
    useEffect(() => {
        if (!autoRefreshComments) return
        // ON 직후 1회 즉시 갱신 + 최신으로 이동
        reloadComments({ scrollToBottom: true })
        const t = window.setInterval(() => {
            reloadComments({ scrollToBottom: true })
        }, 30_000)
        return () => window.clearInterval(t)
    }, [autoRefreshComments, reloadComments])

    // 히스토리 영역: 최초 진입 시 맨 아래로 스크롤
    useEffect(() => {
        if (!historyScrolledOnce && history.length > 0 && historyListRef.current) {
            historyListRef.current.scrollTop = historyListRef.current.scrollHeight
            setHistoryScrolledOnce(true)
        }
    }, [history, historyScrolledOnce])

    // 탭 전환 시 URL 동기화
    useEffect(() => {
        if (activeTargetId) {
            const next = new URLSearchParams(searchParams)
            next.set('targetId', String(activeTargetId))
            setSearchParams(next, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTargetId])

    // ==================================================
    // 🔹 매니저 배정 패널 열기 / 배정 실행
    // ==================================================
    const openAssignmentPanel = async () => {
        if (!(isInsurerUser || isInsurerAdmin)) {
            return show('원수사 사용자만 후보자 목록을 조회할 수 있습니다', 'error')
        }
        setAssignmentLoading(true)
        try {
            const res = await api.get(`/requests/${id}/candidate-managers`)
            setCandidateManagers(Array.isArray(res.data?.data) ? res.data.data : res.data?.data?.items || [])
            setShowAssignmentPanel(true)
        } catch {
            show('후보자 목록을 불러오지 못했습니다', 'error')
        } finally {
            setAssignmentLoading(false)
        }
    }

    const assignManager = async () => {
        if (!selectedManager) return show('매니저를 선택해주세요', 'error')
        const target = (targets || []).find(t => String(t.id) === String(activeTargetId)) || (targets || [])[0]
        if (!target) return show('타겟을 선택하세요', 'error')
        try {
            // 최초 수동 배정 및 재배정 모두 타겟 단위 reassign API 사용
            await api.patch(`/requests/targets/${target.id}/reassign`, {
                newManagerId: selectedManager.id,
                reason: assignmentReason || ''
            })
            show('매니저 배정 완료', 'success')
            setShowAssignmentPanel(false)
            setSelectedManager(null)
            setAssignmentReason('')
            const res = await api.get(`/requests/${id}`)
            setDetail(res.data?.data || res.data)
        } catch {
            show('매니저 배정 실패', 'error')
        }
    }

    // GA/운영자: 외부 공유 링크 생성 (비로그인 열람)
    const createShareLink = async () => {
        const target = currentTarget
        if (!target?.id) return show('타겟을 선택하세요', 'error')
        try {
            const res = await api.post(`/requests/targets/${target.id}/share-link`, {})
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const url = payload?.url
            if (!url) throw new Error('no_url')
            try {
                if (navigator?.clipboard?.writeText) {
                    await navigator.clipboard.writeText(url)
                    show('공유 링크가 복사되었습니다. 링크를 받은 사람은 해당 의뢰 내용을 확인할 수 있습니다.', 'success')
                } else {
                    window.prompt('공유 링크가 생성되었습니다. 클립보드 복사가 지원되지 않아 아래 링크를 직접 복사해 주세요.', url)
                }
            } catch {
                window.prompt('공유 링크가 생성되었습니다. 브라우저 권한 문제로 자동 복사하지 못했습니다. 아래 링크를 직접 복사해 주세요.', url)
            }
        } catch {
            show('공유 링크를 생성하지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error')
        }
    }

    // 공유 링크 UI는 기본 비활성화(운영 정책에 따라 단계적 오픈)
    const enableRequestShareLink = String(process.env.REACT_APP_ENABLE_REQUEST_SHARE_LINK || '').toLowerCase() === 'true'

    const getStatusStyle = (status) => {
        switch (status) {
            case 'ON_DUTY': return 'bg-green-100 text-green-800'
            case 'TRAINING': return 'bg-blue-100 text-blue-800'
            case 'VACATION': return 'bg-yellow-100 text-yellow-800'
            case 'AWAY': return 'bg-red-100 text-red-800'
            default: return 'bg-gray-100 text-gray-800'
        }
    }

    const currentTargetGuidance = (() => {
        if (!currentTarget) return null
        switch (currentTarget.status) {
            case 'RECEIVED':
                return isInsurerUser
                    ? '접수된 의뢰입니다. 내용을 확인한 뒤 진행 상태로 변경하면 GA가 처리 시작을 확인할 수 있습니다.'
                    : '보험사에 의뢰가 접수되었습니다. 담당자가 진행을 시작하면 상태가 업데이트됩니다.'
            case 'ASSIGNED':
                return isInsurerUser
                    ? '담당자 배정이 완료되었습니다. 검토를 시작하면 진행 상태로 변경해 주세요.'
                    : '보험사 담당자에게 배정되었습니다. 추가 요청이 있으면 코멘트로 확인할 수 있습니다.'
            case 'IN_PROGRESS':
                return isInsurerUser
                    ? '처리 중인 의뢰입니다. 완료 처리 전 첨부파일과 코멘트를 한 번 더 확인해 주세요.'
                    : '담당자가 검토 중입니다. 보완 요청이나 완료 알림을 기다려 주세요.'
            case 'RE_REQUESTED':
                return '보완/확인 요청이 등록된 상태입니다. 코멘트와 첨부파일을 확인한 뒤 필요한 내용을 보완해 주세요.'
            case 'QUESTION':
                return '문의가 등록된 상태입니다. 코멘트에서 확인이 필요한 내용을 먼저 확인해 주세요.'
            case 'COMPLETED':
                return view === 'GA'
                    ? '처리가 완료되었습니다. 결과와 첨부파일을 확인한 뒤 필요하면 보완/확인 요청을 남길 수 있습니다.'
                    : '완료 처리된 의뢰입니다. GA 사용자가 결과와 첨부파일을 확인할 수 있습니다.'
            case 'CANCELED':
                return '취소된 의뢰입니다. 담당자는 더 이상 이 보험사 의뢰를 진행할 수 없습니다.'
            default:
                return '상태를 확인한 뒤 필요한 다음 행동을 진행해 주세요.'
        }
    })()

    if (loading) return <Loading />
    if (error) return <InlineError message={error} />
    if (!detail) return null

    return (
        <section className="space-y-6">
            {/* ==================================================
          💡 상단 고정 액션바
          ================================================== */}
            <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-slate-200">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 px-4 md:px-6 py-3">
                    <h1 className="text-xl md:text-2xl font-bold text-slate-800">
                        설계 의뢰 상세 #{detail.id}
                    </h1>
                    <div className="flex flex-wrap justify-end gap-2">
                        {/* GA: 비가입 담당자용 공유 링크 */}
                        {enableRequestShareLink && view === 'GA' && currentTarget && (
                            <button
                                className="bg-slate-200 text-slate-700 px-4 py-2 rounded-md hover:bg-slate-300"
                                onClick={createShareLink}
                            >
                                공유 링크 생성
                            </button>
                        )}
                        {/* 원수사: 버튼은 상단에 유지해도 문맥이 명확(본인 원수사 1개만 처리) */}
                        {isInsurerUser && detail.status === 'RECEIVED' && !detail.managerName && allow.role([
                            Roles.OPERATOR,
                            Roles.INSURER_MASTER_ADMIN,
                            Roles.INSURER_MASTER_DEPUTY_ADMIN,
                            Roles.INSURER_LEADER,
                            Roles.INSURER_DEPUTY_LEADER,
                            Roles.INSURER_OTHER_ADMIN,
                            Roles.INSURER_BRANCH_MANAGER,
                            Roles.INSURER_DEPUTY_BRANCH_MANAGER,
                            Roles.INSURER_MANAGER,
                            Roles.INSURER_EDUCATION_MANAGER,
                            Roles.INSURER_PLAN_MANAGER,
                        ]) && (
                            <button
                                className="bg-brand-navy text-white px-4 py-2 rounded-md hover:bg-brand-navy/90"
                                onClick={openAssignmentPanel}
                                disabled={assignmentLoading}
                            >
                                {assignmentLoading ? '로딩 중...' : '매니저 배정'}
                            </button>
                        )}
                        {isInsurerUser && detail.managerName && allow.role([
                            Roles.OPERATOR,
                            Roles.INSURER_MASTER_ADMIN,
                            Roles.INSURER_MASTER_DEPUTY_ADMIN,
                            Roles.INSURER_LEADER,
                            Roles.INSURER_DEPUTY_LEADER,
                            Roles.INSURER_OTHER_ADMIN,
                            Roles.INSURER_BRANCH_MANAGER,
                            Roles.INSURER_DEPUTY_BRANCH_MANAGER,
                            Roles.INSURER_MANAGER,
                            Roles.INSURER_EDUCATION_MANAGER,
                            Roles.INSURER_PLAN_MANAGER,
                        ]) && (
                            <>
                                {/* 담당자는 "요청만", 관리자는 "재배정 처리" */}
                                {!isInsurerAdmin && currentTarget && (
                                    (() => {
                                        const effectiveId = currentTarget.effectiveManagerId || currentTarget.assignedManagerId
                                        const isAssignee = effectiveId && user?.id && String(effectiveId) === String(user.id)
                                        const canRequest = isAssignee && !['COMPLETED', 'CANCELED'].includes(currentTarget.status)
                                        if (!canRequest) return null
                                        return (
                                            <button
                                                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                                                onClick={async () => {
                                                    const target = currentTarget
                                                    const reason = window.prompt('재배정 요청 사유를 입력해 주세요. 입력한 내용은 담당 관리자에게 공유됩니다.', '') || ''
                                                    try {
                                                        await api.post(`/requests/targets/${target.id}/reassign-request`, { reason, actorUserId: user.id })
                                                        show('재배정 요청이 접수되었습니다. 담당 관리자가 사유를 확인한 뒤 처리합니다.', 'success')
                                                        const res = await api.get(`/requests/${id}`)
                                                        const d = res.data?.data || res.data
                                                        setDetail(d)
                                                        const ts = Array.isArray(d?.targets) ? d.targets : []
                                                        setTargets(ts)
                                                    } catch {
                                                        show('재배정 요청을 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error')
                                                    }
                                                }}
                                            >
                                                재배정 요청
                                            </button>
                                        )
                                    })()
                                )}
                                {isInsurerAdmin && (
                                    <button
                                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                                        onClick={openAssignmentPanel}
                                    >
                                        재배정 처리
                                    </button>
                                )}
                            </>
                        )}
                        {/* 원수사: 진행/완료 (상단 유지) */}
                        {isInsurerUser && allow.role([
                            Roles.OPERATOR,
                            Roles.INSURER_MASTER_ADMIN,
                            Roles.INSURER_MASTER_DEPUTY_ADMIN,
                            Roles.INSURER_LEADER,
                            Roles.INSURER_DEPUTY_LEADER,
                            Roles.INSURER_OTHER_ADMIN,
                            Roles.INSURER_BRANCH_MANAGER,
                            Roles.INSURER_DEPUTY_BRANCH_MANAGER,
                            Roles.INSURER_MANAGER,
                            Roles.INSURER_EDUCATION_MANAGER,
                            Roles.INSURER_PLAN_MANAGER,
                        ]) && currentTarget && currentTarget.status === 'ASSIGNED' && (
                            <button
                                className="bg-amber-500 text-white px-4 py-2 rounded-md hover:bg-amber-600"
                                onClick={async () => {
                                    const target = (targets || []).find(t => String(t.id) === String(activeTargetId)) || (targets || [])[0]
                                    if (!target) return show('타겟을 선택하세요', 'error')
                                    try {
                                        await api.post(`/requests/targets/${target.id}/start`, { actorUserId: user.id })
                                        show('진행 상태로 변경되었습니다', 'success')
                                        const res = await api.get(`/requests/${id}`)
                                        const d = res.data?.data || res.data
                                        setDetail(d)
                                        const ts = Array.isArray(d?.targets) ? d.targets : []
                                        setTargets(ts)
                                    } catch { show('진행 상태 변경에 실패했습니다', 'error') }
                                }}
                            >
                                진행
                            </button>
                        )}
                        {isInsurerUser && allow.role([
                            Roles.OPERATOR,
                            Roles.INSURER_MASTER_ADMIN,
                            Roles.INSURER_MASTER_DEPUTY_ADMIN,
                            Roles.INSURER_LEADER,
                            Roles.INSURER_DEPUTY_LEADER,
                            Roles.INSURER_OTHER_ADMIN,
                            Roles.INSURER_BRANCH_MANAGER,
                            Roles.INSURER_DEPUTY_BRANCH_MANAGER,
                            Roles.INSURER_MANAGER,
                            Roles.INSURER_EDUCATION_MANAGER,
                            Roles.INSURER_PLAN_MANAGER,
                        ]) && currentTarget && ['IN_PROGRESS', 'RE_REQUESTED'].includes(currentTarget.status) && (
                            <>
                                <button
                                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                                    onClick={async () => {
                                        const target = (targets || []).find(t => String(t.id) === String(activeTargetId)) || (targets || [])[0]
                                        if (!target) return show('타겟을 선택하세요', 'error')
                                        try {
                                            await api.post(`/requests/targets/${target.id}/complete`, { designNumber: null, fileId: null })
                                            show('완료 처리되었습니다. GA 사용자가 결과와 첨부를 확인할 수 있습니다.', 'success')
                                            const res = await api.get(`/requests/${id}`)
                                            const d = res.data?.data || res.data
                                            setDetail(d)
                                            const ts = Array.isArray(d?.targets) ? d.targets : []
                                            setTargets(ts)
                                        } catch { show('완료 처리에 실패했습니다. 상태를 확인한 뒤 다시 시도해 주세요.', 'error') }
                                    }}
                                >
                                    완료 처리
                                </button>
                                <button
                                    className="bg-slate-200 text-slate-700 px-4 py-2 rounded-md hover:bg-slate-300"
                                    onClick={async () => {
                                        try {
                                            await api.post(`/requests/${id}/approve-completion`)
                                            show('완료 승인 요청(데모) 전송', 'success')
                                        } catch { show('완료 승인 실패(데모)', 'error') }
                                    }}
                                >
                                    완료 승인
                                </button>
                            </>
                        )}
                        {/* GA 본인 생성 의뢰 취소: RECEIVED 상태에서만 노출 */}
                        {view === 'GA' && detail.status === 'RECEIVED' && (
                            <button
                                className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 w-full md:w-auto"
                                onClick={async () => {
                                    const ok = window.confirm('이 설계의뢰 전체를 취소할까요?\n취소 후에는 담당자가 더 이상 이 의뢰를 진행할 수 없습니다.')
                                    if (!ok) return
                                    try {
                                        await api.patch(`/requests/${id}/cancel`, { actorUserId: user?.id })
                                        show('설계의뢰가 취소되었습니다. 담당자는 더 이상 이 의뢰를 진행할 수 없습니다.', 'success')
                                    } catch { show('의뢰를 취소하지 못했습니다. 현재 상태를 확인한 뒤 다시 시도해 주세요.', 'error') }
                                }}
                            >
                               의뢰 전체 취소
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* ==================================================
          💡 의뢰 개요
          ================================================== */}
            <div className="rounded-xl bg-slate-50 p-5 shadow-sm mb-4">
                <h2 className="font-semibold text-slate-700 mb-2">개요</h2>
                <div className="text-sm text-slate-700 space-y-1">
                    <div>제목: {detail.title}</div>
                    <div className="whitespace-pre-wrap break-words">공통 메모: {detail.commonNote ?? '-'}</div>
                    <div>기등록 고객(공통): {detail.commonExistingCustomer ? 'YES' : 'NO'}</div>
                </div>
            </div>

            {/* ==================================================
          💡 타겟 & 의뢰 상태
          ================================================== */}
            <div className="rounded-xl bg-slate-50 p-5 shadow-sm mb-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <h2 className="font-semibold text-slate-700">{isInsurerUser ? '진행 정보' : '의뢰 대상 보험사'}</h2>
                    {/* 선택된 보험사(타겟) 기준 액션 버튼들 */}
                    <div className="flex flex-wrap justify-end gap-2">
                        {/* 원수사 액션은 상단으로 이동 (이 구간에는 GA 타겟 액션만 둔다) */}
                        {/* GA: 타겟별 보완/확인 요청 (완료된 타겟만) */}
                        {view === 'GA' && currentTarget && currentTarget.status === 'COMPLETED' && (
                            <button
                                className="bg-orange-500 text-white px-3 py-2 rounded-md hover:bg-orange-600 text-sm"
                                onClick={async () => {
                                    const target = currentTarget
                                    const reason = window.prompt('보완/확인 요청 사유를 입력해 주세요. 입력한 내용은 담당자에게 공유됩니다.', '') || ''
                                    try {
                                        await api.post(`/requests/targets/${target.id}/re-request`, { reason, actorUserId: user?.id })
                                        show('보완/확인 요청이 접수되었습니다. 담당자가 사유를 확인할 수 있습니다.', 'success')
                                        const res = await api.get(`/requests/${id}`)
                                        const d = res.data?.data || res.data
                                        setDetail(d)
                                        const ts = Array.isArray(d?.targets) ? d.targets : []
                                        setTargets(ts)
                                    } catch { show('보완/확인 요청을 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error') }
                                }}
                            >
                                재요청
                            </button>
                        )}
                        {/* GA: 선택된 보험사(타겟) 개별 취소 */}
                        {view === 'GA' && currentTarget && !['COMPLETED', 'CANCELED'].includes(currentTarget.status) && (
                            <button
                                className="bg-red-200 text-red-700 px-3 py-2 rounded-md hover:bg-red-300 text-sm"
                                onClick={async () => {
                                    const target = currentTarget
                                    if (!target) return show('타겟을 선택하세요', 'error')
                                    const insurerName = target?.insurerCompanyName || target?.insurerOrgName || target?.insurerOrgPath || target?.insurerOrgId || '선택된 보험사'
                                    const ok = window.confirm(`${insurerName} 보험사에 대한 설계의뢰를 취소할까요?\n이 보험사 담당자는 더 이상 해당 의뢰를 진행할 수 없습니다.`)
                                    if (!ok) return
                                    try {
                                        await api.patch(`/requests/targets/${target.id}/cancel`, { actorUserId: user?.id })
                                        show('선택한 보험사 의뢰가 취소되었습니다. 다른 보험사 의뢰는 그대로 유지됩니다.', 'success')
                                        const res = await api.get(`/requests/${id}`)
                                        const d = res.data?.data || res.data
                                        setDetail(d)
                                        const ts = Array.isArray(d?.targets) ? d.targets : []
                                        setTargets(ts)
                                    } catch { show('선택한 보험사 의뢰를 취소하지 못했습니다. 현재 상태를 확인한 뒤 다시 시도해 주세요.', 'error') }
                                }}
                            >
                                선택된 보험사 의뢰 취소
                            </button>
                        )}
                    </div>
                </div>
                {/* 탭형 칩 */}
                {!isInsurerUser && (
                    <div className="flex flex-wrap gap-2 mb-3">
                        {(displayTargets || []).map(t => (
                            <button
                                key={t.id}
                                className={`px-3 py-1 rounded-full text-sm border ${
                                    activeTargetId === t.id
                                        ? (t.status === 'CANCELED' ? 'bg-red-100 text-red-800 border-red-300' : 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10')
                                        : (t.status === 'CANCELED' ? 'bg-slate-100 text-slate-500 border-slate-200 line-through' : 'bg-white text-slate-700 border-slate-300')
                                }`}
                                onClick={() => setActiveTargetId(t.id)}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <span>{t.insurerCompanyName || t.insurerOrgName || t.insurerOrgPath || t.insurerOrgId}</span>
                                    {(() => {
                                        const n = t?.requestAssignedNotification
                                        if (!n) return null
                                        const ch = String(n.channel || '').toUpperCase()
                                        const st = String(n.status || '').toUpperCase()
                                        if (st === 'SENT' && ch === 'KAKAO') {
                                            return <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px]">알림톡</span>
                                        }
                                        if (st === 'SENT' && ch === 'SMS') {
                                            return <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px]">문자</span>
                                        }
                                        if (st === 'FAILED') {
                                            return (
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    className="rounded bg-red-100 text-red-800 px-1.5 py-0.5 text-[10px] hover:bg-red-200 cursor-pointer"
                                                    title="클릭하여 실패 원인 확인 / 재발송"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        openNotifDetail(t)
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            openNotifDetail(t)
                                                        }
                                                    }}
                                                >
                                                    발송실패
                                                </span>
                                            )
                                        }
                                        if (st === 'SKIPPED') {
                                            return <span className="rounded bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[10px]">미발송</span>
                                        }
                                        return null
                                    })()}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                {currentTarget ? (
                    <div className="text-sm text-slate-700 space-y-1">
                        {!isInsurerUser && (
                            <div>원수사: {currentTarget.insurerCompanyName || currentTarget.insurerOrgName || currentTarget.insurerOrgPath || currentTarget.insurerOrgId || '-'}</div>
                        )}
                        <div>상태: {RequestStatusLabel[currentTarget.status] || currentTarget.status}</div>
                        {currentTargetGuidance ? (
                            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-900">
                                {currentTargetGuidance}
                            </div>
                        ) : null}
                        {(() => {
                            const n = currentTarget?.requestAssignedNotification
                            const st = String(n?.status || '').toUpperCase()
                            if (st !== 'FAILED') return null
                            return (
                                <div className="mt-1">
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className="inline-flex items-center rounded bg-red-100 text-red-800 px-2 py-1 text-xs hover:bg-red-200 cursor-pointer"
                                        title="클릭하여 실패 원인 확인 / 재발송"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            openNotifDetail(currentTarget)
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                openNotifDetail(currentTarget)
                                            }
                                        }}
                                    >
                                        알림톡 발송 실패(확인/재발송)
                                    </span>
                                </div>
                            )
                        })()}
                        {view === 'GA' && ['ASSIGNED', 'IN_PROGRESS'].includes(currentTarget.status) && (
                            <div>
                                담당자: {currentTarget.effectiveManagerName || currentTarget.assignedManagerName || '-'}
                                {currentTarget.isDelegated ? (
                                    <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700 bg-indigo-100">
                                        대직
                                    </span>
                                ) : null}
                            </div>
                        )}
                        <div>기등록 고객: {currentTarget.existingCustomer ? 'YES' : 'NO'}</div>
                        <div>개별 메모: {currentTarget.note ?? '-'}</div>
                        {view === 'GA' && !currentTarget.assignedManagerId && currentTarget.status === 'RECEIVED' && currentTarget.unassignedReason && (
                            <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                                {currentTarget.unassignedReason}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-sm text-slate-500">선택된 타겟이 없습니다.</div>
                )}
            </div>

            {/* ==================================================
              💡 알림톡 발송 실패 상세/재발송 모달
              ================================================== */}
            {notifModalOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
                    onClick={() => {
                        setNotifModalOpen(false)
                        setNotifModalTarget(null)
                        setNotifDetail(null)
                        setNotifError(null)
                        setNotifLoading(false)
                    }}
                >
                    <div
                        className="w-full max-w-2xl rounded-lg bg-white shadow-lg p-4 md:p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="text-base font-semibold text-slate-800">알림톡 발송 실패 원인</div>
                                <div className="text-xs text-slate-500 mt-1">
                                    타겟: {notifModalTarget?.insurerCompanyName || notifModalTarget?.insurerOrgName || notifModalTarget?.insurerOrgPath || notifModalTarget?.insurerOrgId || '-'}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="text-sm px-2 py-1 rounded border hover:bg-slate-50"
                                onClick={() => {
                                    setNotifModalOpen(false)
                                    setNotifModalTarget(null)
                                    setNotifDetail(null)
                                    setNotifError(null)
                                    setNotifLoading(false)
                                }}
                            >
                                닫기
                            </button>
                        </div>

                        <div className="mt-3 space-y-2 text-sm">
                            {notifLoading && (
                                <div className="text-slate-500">불러오는 중...</div>
                            )}
                            {notifError && (
                                <div className="text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                                    {notifError}
                                </div>
                            )}

                            {notifDetail && (
                                (() => {
                                    const parsed = safeParseJson(notifDetail?.message)
                                    const channelResults = parsed?.channelResults || null
                                    const kakao = channelResults?.KAKAO || null
                                    const sms = channelResults?.SMS || null
                                    return (
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <div className="rounded border bg-slate-50 px-3 py-2">
                                                    <div className="text-xs text-slate-500">상태</div>
                                                    <div className="font-medium">{notifDetail.status || '-'}</div>
                                                </div>
                                                <div className="rounded border bg-slate-50 px-3 py-2">
                                                    <div className="text-xs text-slate-500">에러코드</div>
                                                    <div className="font-medium">{notifDetail.errorCode || parsed?.errorCode || '-'}</div>
                                                </div>
                                                <div className="rounded border bg-slate-50 px-3 py-2">
                                                    <div className="text-xs text-slate-500">채널</div>
                                                    <div className="font-medium">{notifDetail.channel || parsed?.channel || '-'}</div>
                                                </div>
                                                <div className="rounded border bg-slate-50 px-3 py-2">
                                                    <div className="text-xs text-slate-500">발생시각</div>
                                                    <div className="font-medium">{notifDetail.createdAt ? formatDateTime(notifDetail.createdAt) : '-'}</div>
                                                </div>
                                            </div>

                                            {(kakao || sms) && (
                                                <div className="rounded border px-3 py-2">
                                                    <div className="text-xs text-slate-500 mb-1">채널별 결과</div>
                                                    {kakao && (
                                                        <div className="text-xs">
                                                            <span className="font-semibold">KAKAO</span>
                                                            <span className="ml-2">success={String(!!kakao.success)}</span>
                                                            <span className="ml-2">errorCode={kakao.errorCode || '-'}</span>
                                                            <div className="mt-1 break-words text-slate-700">response: {kakao.response || '-'}</div>
                                                        </div>
                                                    )}
                                                    {sms && (
                                                        <div className="text-xs mt-2">
                                                            <span className="font-semibold">SMS</span>
                                                            <span className="ml-2">success={String(!!sms.success)}</span>
                                                            <span className="ml-2">errorCode={sms.errorCode || '-'}</span>
                                                            <div className="mt-1 break-words text-slate-700">response: {sms.response || '-'}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    className="bg-brand-navy text-white px-3 py-2 rounded hover:bg-brand-navy/90 text-sm disabled:opacity-60"
                                                    onClick={resendNotif}
                                                    disabled={notifLoading}
                                                >
                                                    재발송
                                                </button>
                                                <button
                                                    type="button"
                                                    className="px-3 py-2 rounded border hover:bg-slate-50 text-sm"
                                                    onClick={async () => {
                                                        const raw = notifDetail?.message || ''
                                                        try {
                                                            if (navigator?.clipboard?.writeText) {
                                                                await navigator.clipboard.writeText(raw)
                                                                show('실패 상세(JSON)가 복사되었습니다', 'success')
                                                            } else {
                                                                // eslint-disable-next-line no-alert
                                                                alert(raw)
                                                            }
                                                        } catch {
                                                            // eslint-disable-next-line no-alert
                                                            alert(raw)
                                                        }
                                                    }}
                                                >
                                                    실패 상세 복사
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })()
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ==================================================
          💡 코멘트 영역
          ================================================== */}
            <div className="rounded-xl bg-white p-4 md:p-5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-700">
                        <MessageSquare className="h-4 w-4 text-sgez-primary" aria-hidden="true" />
                        코멘트
                    </h2>
                    <div className="flex flex-wrap items-center gap-2 md:gap-3">
                        <div className="flex items-center gap-2 text-xs text-slate-600">
                            <span className="font-medium">자동 새로고침</span>
                            <label className="inline-flex items-center gap-1.5">
                                <input
                                    type="radio"
                                    name="auto_refresh_comments"
                                    checked={autoRefreshComments}
                                    onChange={() => setAutoRefreshComments(true)}
                                />
                                <span>ON</span>
                            </label>
                            <label className="inline-flex items-center gap-1.5">
                                <input
                                    type="radio"
                                    name="auto_refresh_comments"
                                    checked={!autoRefreshComments}
                                    onChange={() => setAutoRefreshComments(false)}
                                />
                                <span>OFF</span>
                            </label>
                        </div>
                        <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border text-slate-600 hover:bg-slate-50"
                            onClick={reloadComments}
                        >
                            새로고침
                        </button>
                        <button
                            type="button"
                            className="text-xs px-2 py-1 rounded border text-slate-600 hover:bg-slate-50"
                            onClick={() => setShowAllComments(v => !v)}
                        >
                            {showAllComments ? '선택 타겟 코멘트만 보기' : '전체 코멘트 보기'}
                        </button>
                    </div>
                </div>
                <div
                    ref={commentsListRef}
                    className="mt-3 max-h-60 overflow-auto space-y-2 text-sm"
                >
                    {comments
                        // 타겟 기준 필터
                        .filter(c => {
                            if (showAllComments || !activeTargetId) return true
                            return c.requestTargetId && String(c.requestTargetId) === String(activeTargetId)
                        })
                        // 원수사 사용자일 경우: 본인 원수사 코멘트 + GA 코멘트만 노출 (보안은 BE에서 한 번 더 필터링)
                        .filter(c => {
                            if (!isInsurerUser || !user?.orgId) return true
                            const orgType = c.authorOrgType
                            const orgId = c.authorOrgId
                            if (orgType === 'GA') return true
                            if (orgType === 'INSURER' && orgId && String(orgId) === String(user.orgId)) return true
                            return false
                        })
                        .map(c => {
                            const canEdit = user?.id && String(user.id) === String(c.authorId)
                            const isQ = !!c.isQuestion
                            const isResolved = !!c.questionResolved
                            const isReRequest = typeof c.message === 'string' && c.message.startsWith('[재요청')
                            const canResolve = isQ && !isResolved && user?.id && allow?.role && allow.role([
                                Roles.OPERATOR,
                                Roles.GA_PLANNER, Roles.GA_MANAGER, Roles.GA_LEADER,
                                Roles.INSURER_MANAGER, Roles.INSURER_BRANCH_MANAGER, Roles.INSURER_DEPUTY_BRANCH_MANAGER, Roles.INSURER_EDUCATION_MANAGER, Roles.INSURER_PLAN_MANAGER, Roles.INSURER_LEADER,
                            ])
                            const displayMessage = isReRequest
                                ? (c.message || '').replace(/^\[재요청\]\s*/, '')
                                : c.message
                            return (
                                <div key={c.id} className={`border rounded-lg px-3 py-2 ${isQ ? 'bg-amber-50' : 'bg-slate-50'}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="text-slate-600">
                                                {c.authorName} · {formatDateTime(c.createdAt)}
                                                {c.visibility && (
                                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${c.visibility === 'INTERNAL' ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-800'}`}>
                                                        {c.visibility === 'INTERNAL' ? '비공개' : '공개'}
                                                    </span>
                                                )}
                                                {isQ && (
                                                    <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 bg-amber-100">
                                                        문의
                                                    </span>
                                                )}
                                                {isResolved && (
                                                    <span className="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 bg-emerald-100">
                                                        해결완료
                                                    </span>
                                                )}
                                                {isReRequest && (
                                                    <span className="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold text-orange-700 bg-orange-100">
                                                        재요청
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 text-slate-800">{displayMessage}</div>
                                        </div>
                                        {(canEdit || canResolve) && (
                                            <div className="flex flex-col gap-1 text-xs text-slate-500 items-end">
                                                {canEdit && (
                                                    <>
                                                        <button
                                                            className="hover:underline"
                                                            onClick={() => {
                                                                setEditingCommentId(c.id)
                                                                setCommentText(c.message || '')
                                                            }}
                                                        >
                                                            수정
                                                        </button>
                                                        <button
                                                            className="text-red-500 hover:underline"
                                                            onClick={async () => {
                                                                if (!window.confirm('이 코멘트를 삭제할까요?\n삭제 후에는 관계자가 더 이상 내용을 확인할 수 없습니다.')) return
                                                                try {
                                                                    await api.delete(`/requests/${id}/comments/${c.id}`, { data: { actorUserId: user.id } })
                                                                    await reloadComments()
                                                                    show('코멘트가 삭제되었습니다.', 'success')
                                                                } catch {
                                                                    show('코멘트를 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error')
                                                                }
                                                            }}
                                                        >
                                                            삭제
                                                        </button>
                                                    </>
                                                )}
                                                {canResolve && (
                                                    <button
                                                        className="text-emerald-600 hover:underline"
                                                        onClick={async () => {
                                                            const targetId = activeTargetId || currentTarget?.id
                                                            if (!targetId) return show('문의 상태를 해제할 타겟을 먼저 선택하세요', 'error')
                                                            try {
                                                                await api.post(`/requests/targets/${targetId}/resolve-question`, { actorUserId: user.id })
                                                                const res = await api.get(`/requests/${id}`)
                                                                const d = res.data?.data || res.data
                                                                setDetail(d)
                                                                const ts = Array.isArray(d?.targets) ? d.targets : []
                                                                setTargets(ts)
                                                                await reloadComments()
                                                                show('문의 상태를 해제했습니다', 'success')
                                                            } catch {
                                                                show('문의 상태 해제에 실패했습니다', 'error')
                                                            }
                                                        }}
                                                    >
                                                        해결
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    {comments.length === 0 && <div className="text-slate-500">코멘트가 없습니다</div>}
                </div>

                <form className="mt-3 flex gap-2 flex-wrap items-center"
                    onSubmit={async (e) => {
                        e.preventDefault()
                        if (!commentText.trim()) return
                        if (!user?.id) return show('로그인 정보를 확인할 수 없습니다.', 'error')
                        try {
                            if (editingCommentId) {
                                await api.patch(`/requests/${id}/comments/${editingCommentId}`, {
                                    message: commentText.trim(),
                                    actorUserId: user.id,
                                })
                                show('코멘트가 수정되었습니다. 공개 범위는 기존 설정을 유지합니다.', 'success')
                            } else {
                                // GA는 공개만 허용
                                const visibility = (view === 'GA') ? 'EXTERNAL' : commentVisibility
                                await api.post(`/requests/${id}/comments`, {
                                    message: commentText.trim(),
                                    visibility,
                                    authorUserId: user.id,
                                    requestTargetId: applyToAllTargets ? null : activeTargetId,
                                    isQuestion: isQuestion && !!activeTargetId,
                                    applyToAllTargets: (view === 'GA') ? !!applyToAllTargets : false,
                                })
                                show(visibility === 'INTERNAL' ? '비공개 코멘트가 추가되었습니다. 내부자에게만 노출됩니다.' : '코멘트가 추가되었습니다. 관계자에게 공유됩니다.', 'success')
                            }
                            setCommentText('')
                            setEditingCommentId(null)
                            setIsQuestion(false)
                            setApplyToAllTargets(false)
                                await reloadComments()
                        } catch {
                            show(editingCommentId ? '코멘트 수정 실패' : '코멘트 추가 실패', 'error')
                        }
                    }}>
                    <input className="flex-1 rounded border px-3 py-2 text-sm" placeholder="코멘트를 입력하세요" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                    {view === 'GA' && Array.isArray(user?.roles) && user.roles.some(r => String(r).startsWith('GA_')) && Array.isArray(targets) && targets.length > 1 && (
                        <label className="flex items-center gap-1 text-xs text-slate-600">
                            <input
                                type="checkbox"
                                checked={applyToAllTargets}
                                onChange={(e) => setApplyToAllTargets(e.target.checked)}
                            />
                            <span>전체 보험사에 동일 코멘트</span>
                        </label>
                    )}
                    {view !== 'GA' && (
                        <>
                            <select className="rounded border px-3 py-2 text-sm" value={commentVisibility} onChange={(e) => setCommentVisibility(e.target.value)}>
                                <option value="EXTERNAL">공개</option>
                                <option value="INTERNAL">비공개</option>
                            </select>
                            <label className="flex items-center gap-1 text-xs text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={isQuestion}
                                    disabled={!activeTargetId}
                                    onChange={(e) => setIsQuestion(e.target.checked)}
                                />
                                <span>문의</span>
                            </label>
                        </>
                    )}
                    <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm hover:bg-brand-navy/90">
                        {editingCommentId ? '수정 완료' : '전송'}
                    </button>
                </form>
                <div className="mt-2 text-xs text-slate-500">
                    공개 코멘트는 의뢰 관계자에게 공유됩니다. 비공개 코멘트는 내부자 전용이며 GA 사용자는 공개 코멘트만 작성할 수 있습니다.
                </div>
            </div>

            {/* ==================================================
          💡 변경이력(히스토리) 영역
          ================================================== */}
            <div className="rounded-xl bg-white p-5 shadow-sm">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
                    <History className="h-4 w-4 text-sgez-primary" aria-hidden="true" />
                    변경이력
                </h2>
                <ul ref={historyListRef} className="mt-3 max-h-60 overflow-auto text-sm space-y-2">
                    {history.map(h => (
                        <li key={h.id} className="border rounded-lg px-3 py-2 bg-slate-50">
                            <div className="font-medium text-slate-800">
                                {(h.action === 'CREATED' && '생성됨') ||
                                    (h.action === 'ASSIGNED' && '배정됨') ||
                                    (h.action === 'STATUS_CHANGED' && '상태 변경') ||
                                    (h.action === 'CANCELED' && '취소됨') ||
                                    (h.action === 'COMPLETED' && '완료됨') ||
                                    (h.action === 'REQUEST_TARGET_QUESTION' && '문의 등록') ||
                                    (h.action === 'REQUEST_TARGET_QUESTION_RESOLVED' && '문의 해결') ||
                                    (h.action === 'REQUEST_REREQUEST' && '보완/확인요청') ||
                                    h.action || h.event}
                            </div>
                            <div className="text-slate-600">
                                {h.description || h.message} · {formatDateTime(h.createdAt)}
                            </div>
                        </li>
                    ))}
                    {history.length === 0 && <li className="text-slate-500">변경이력이 없습니다</li>}
                </ul>
            </div>

            {/* ==================================================
          💡 첨부파일 영역
          ================================================== */}
            <div className="rounded-xl bg-white p-5 shadow-sm">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
                    <Paperclip className="h-4 w-4 text-sgez-primary" aria-hidden="true" />
                    첨부 파일
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                    첨부파일은 이 설계의뢰 관계자에게만 공유됩니다. 주민등록번호, 계좌번호 등 불필요한 민감정보가 포함되지 않았는지 업로드 전 확인해 주세요.
                </p>
                <div className="mt-3 text-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <input
                            type="file"
                            disabled={uploading}
                            onChange={async (e) => {
                                const file = e.target.files && e.target.files[0]
                                if (!file) return
                                setUploading(true)
                                setUploadProgress(0)
                                try {
                                    // 1) 백엔드로 직접 업로드 (로컬 스토리지 + 필요 시 S3 프리사인 생성)
                                    const form = new FormData()
                                    form.append('file', file)
                                    const up = await api.post('/files', form, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                    })
                                    const payload = up?.data?.data ?? up?.data ?? {}
                                    const fileId = payload.fileId || payload.id || payload.filename
                                    if (!fileId) throw new Error('no_file_id')
                                    setUploadProgress(70)

                                    // 2) 의뢰 첨부로 연결
                                    await api.post(`/requests/${id}/attachments`, {
                                        fileId,
                                        filename: file.name,
                                        contentType: file.type,
                                        size: file.size,
                                        authorUserId: user?.id,
                                    })
                                    setUploadProgress(100)

                                    // 3) 최신 첨부 목록 갱신
                                    const a = await api.get(`/requests/${id}/attachments`)
                                    setAttachments(Array.isArray(a.data?.data) ? a.data.data : a.data?.data?.items || [])
                                    show('첨부파일이 업로드되었습니다. 이 의뢰 관계자에게만 공유됩니다.', 'success')
                                } catch {
                                    show('첨부파일을 업로드하지 못했습니다. 파일 용량과 네트워크 상태를 확인해 주세요.', 'error')
                                } finally {
                                    setUploading(false)
                                    setUploadProgress(0)
                                    e.target.value = ''
                                }
                            }}
                        />
                        {uploading && (
                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                <div className="w-20 bg-slate-200 rounded-full h-2">
                                    <div
                                        className="bg-brand-navy h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <span>{uploadProgress}%</span>
                            </div>
                        )}
                    </div>

                    {attachments.length === 0 ? (
                        <div className="text-slate-500">첨부 파일이 없습니다</div>
                    ) : (
                        <div className="space-y-2">
                            {attachments.map(att => (
                                <div key={att.id} className="flex items-center justify-between p-2 border rounded">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm">{att.filename}</span>
                                        <span className="text-xs text-slate-500">
                                            {att.size ? `${(att.size / 1024).toFixed(1)}KB` : ''}
                                        </span>
                                        <span className="text-xs text-slate-500">{formatDateTime(att.createdAt)}</span>
                                        {att.expired && (
                                          <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                                            다운로드 기간만료
                                          </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {att.fileId && !att.expired && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="underline text-slate-700 text-xs"
                                                    onClick={() => {
                                                        try {
                                                            const base = (api.defaults && api.defaults.baseURL)
                                                                ? api.defaults.baseURL.replace(/\/$/, '')
                                                                : ''
                                                            const url = `${base}/files/${att.fileId}/preview`
                                                            window.open(url, '_blank', 'noopener,noreferrer')
                                                        } catch {
                                                            show('미리보기를 열 수 없습니다', 'error')
                                                        }
                                                    }}
                                                >
                                                    미리보기
                                                </button>
                                                <button
                                                    type="button"
                                                    className="underline text-slate-700 text-xs"
                                                    onClick={async () => {
                                                        try {
                                                            const res = await api.get(`/files/${att.fileId}/preview`, { responseType: 'blob' })
                                                            const blobUrl = URL.createObjectURL(res.data)
                                                            const a = document.createElement('a')
                                                            a.href = blobUrl
                                                            a.download = att.filename || att.fileId || 'download'
                                                            document.body.appendChild(a)
                                                            a.click()
                                                            a.remove()
                                                            URL.revokeObjectURL(blobUrl)
                                                        } catch {
                                                            show('파일을 다운로드하지 못했습니다. 권한 또는 다운로드 기간을 확인해 주세요.', 'error')
                                                        }
                                                    }}
                                                >
                                                    다운로드
                                                </button>
                                            </>
                                        )}
                                        <button
                                            className="text-red-600 hover:underline text-xs"
                                            onClick={async () => {
                                                if (!window.confirm('이 첨부파일을 삭제할까요?\n삭제 후에는 의뢰 관계자가 이 파일을 다운로드할 수 없습니다.')) return
                                                try {
                                                    await api.delete(`/requests/${id}/attachments/${att.id}`)
                                                    const a = await api.get(`/requests/${id}/attachments`)
                                                    setAttachments(Array.isArray(a.data?.data) ? a.data.data : a.data?.data?.items || [])
                                                    show('첨부파일이 삭제되었습니다. 의뢰 관계자는 더 이상 이 파일을 다운로드할 수 없습니다.', 'success')
                                                } catch {
                                                    show('첨부파일을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.', 'error')
                                                }
                                            }}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ==================================================
          💡 매니저 배정 모달
          ================================================== */}
            {showAssignmentPanel && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowAssignmentPanel(false)}>
                    <div className="w-full max-w-4xl rounded bg-white p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">매니저 재배정 요청</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => setShowAssignmentPanel(false)}>✕</button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <h4 className="font-medium">후보자 목록</h4>
                                    <label className="flex items-center gap-2 text-xs text-slate-600">
                                        <input
                                            type="checkbox"
                                            checked={candidateFilterAdminOrMapped}
                                            onChange={(e) => setCandidateFilterAdminOrMapped(e.target.checked)}
                                        />
                                        관리자 + 해당 GA담당자만 보기
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {candidateManagers
                                        .filter(m => !candidateFilterAdminOrMapped || !!m.isAdmin || !!m.isGaMapped)
                                        // 현재 배정된 매니저는 후보 목록에서 제외
                                        .filter(m => !(currentTarget?.assignedManagerId && String(m.id) === String(currentTarget.assignedManagerId)))
                                        .map(manager => (
                                        <div
                                            key={manager.id}
                                            className={`border rounded-xl p-3 cursor-pointer transition-all hover:shadow-sm ${selectedManager?.id === manager.id ? 'border-brand-navy bg-blue-50' : 'border-gray-200'}`}
                                            onClick={() => setSelectedManager(manager)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="font-medium">{manager.name}</div>
                                                    <div className="text-sm text-slate-600">{manager.position || '—'}</div>
                                                    <div className="text-xs text-slate-500">{manager.orgName}</div>
                                                    {(manager.isAdmin || manager.isGaMapped) && (
                                                        <div className="mt-1 flex flex-wrap gap-1">
                                                            {manager.isAdmin && (
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-800">관리자</span>
                                                            )}
                                                            {manager.isGaMapped && (
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800">GA담당</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(manager.status)}`}>
                                                        {!manager.status ? '미설정' :
                                                            manager.status === 'ON_DUTY' ? '근무중' :
                                                                manager.status === 'TRAINING' ? '교육중' :
                                                                    manager.status === 'VACATION' ? '휴가중' : '부재중'}
                                                    </span>
                                                    {manager.isRecommended && (
                                                        <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">추천</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <span className="text-slate-600">평균 응답</span>
                                                    <div className="font-medium">{manager.avgResponseTime || '—'}시간</div>
                                                </div>
                                                <div>
                                                    <span className="text-slate-600">처리 건수</span>
                                                    <div className="font-medium">{manager.completionCount || 0}건</div>
                                                </div>
                                            </div>
                                            {manager.substituteName && (
                                                <div className="mt-2 text-xs text-slate-600">
                                                    대직자: {manager.substituteName}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {selectedManager && (
                                <div>
                                    <label className="block text-slate-600 font-medium mb-2">배정 사유</label>
                                    <textarea
                                        className="w-full rounded border px-3 py-2 text-sm"
                                        rows={3}
                                        placeholder="배정 사유를 입력하세요"
                                        value={assignmentReason}
                                        onChange={(e) => setAssignmentReason(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setShowAssignmentPanel(false)}>취소</button>
                            <button className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50" onClick={assignManager} disabled={!selectedManager}>
                                요청
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}
